import { formatTopicTitle } from "../components/OverviewChart/chartPrimitives.js";
import { textCell } from "./csv-cells.js";

/** Header of the first column — matches the My Brand chart export. */
const CONCEPT_COLUMN = "Concept";

const pctKey = (bucket: string) => `${bucket}Pct`;
const metricKey = (bucket: string) => `${bucket}Metric`;

export interface CompetitorCsvSeries {
  key: string;
  label: string;
}

/**
 * One pivoted topic as the Competitors chart plots it: `topic` plus
 * `${bucket}Pct` / `${bucket}Metric` (null = no data for that series).
 */
export interface CompetitorCsvRow {
  topic: string;
  [key: string]: string | number | null;
}

/**
 * Asset % only: `toPrecision` strips IEEE artifacts like
 * `0.29 * 100 → 28.999999999999996` without rounding to the tooltip's integer percent.
 */
function percentCell(value: string | number | null | undefined): string {
  if (typeof value !== "number") return "";
  return String(Number(value.toPrecision(12)));
}

/** Unformatted number so spreadsheets can compute on it; empty cell for no-data. */
function numericCell(value: string | number | null | undefined): string {
  return typeof value === "number" ? String(value) : "";
}

function slugifyMetric(metric: string): string {
  return metric
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Serialize the plotted Competitors Overview rows — concepts × series — as CSV.
 *
 * Each series contributes two columns: the Asset % bar (already 0–100 as drawn)
 * and the selected performance-metric value (the dot). Null buckets stay empty
 * cells, distinct from a real zero.
 */
export function buildCompetitorChartCsv(
  data: CompetitorCsvRow[],
  series: CompetitorCsvSeries[],
  performanceMetric: string,
): string {
  const header = [
    CONCEPT_COLUMN,
    ...series.flatMap((s) => [`${s.label} Asset %`, `${s.label} ${performanceMetric}`]),
  ].map(textCell);

  const rows = data.map((datum) => [
    textCell(formatTopicTitle(datum.topic)),
    ...series.flatMap((s) => [
      percentCell(datum[pctKey(s.key)]),
      numericCell(datum[metricKey(s.key)]),
    ]),
  ]);

  return [header, ...rows].map((row) => row.join(",")).join("\n");
}

/**
 * `competitors-overview-asset-pct-vs-days-assets-2026-09-02.csv` — metric slug so
 * a Days export does not overwrite a Days/Assets one, dated so repeats do not collide.
 */
export function competitorChartCsvFilename(
  performanceMetric: string,
  now: Date = new Date(),
): string {
  return `competitors-overview-asset-pct-vs-${slugifyMetric(performanceMetric)}-${now.toISOString().slice(0, 10)}.csv`;
}
